export interface DetallePedido {
  detalleId: number;
  pedidoId: number;
  productoId: number | null;
  nombreProducto: string;
  precioUnitario: number;
  cantidad: number;
  subtotal: number;
  imagenUrl: string | null;
}

export interface CreatePedidoDetalleDto {
  productoId?: number | null;
  nombreProducto: string;
  precioUnitario: number;
  cantidad: number;
  imagenUrl?: string | null;
}

export interface Seguimiento {
  seguimientoId: number;
  pedidoId: number;
  estatus: string;
  descripcion: string | null;
  latitud: number | null;
  longitud: number | null;
  fechaCreacion: string;
}

export interface Pedido {
  pedidoId: number;
  clienteId: number;
  nombreCliente: string;
  telefonoCliente: string;
  negocioId: number;
  nombreNegocio: string;
  empleadoId: number | null;
  nombreEmpleado: string | null;
  numeroPedido: string;
  direccionEntrega: string;
  latitud: number | null;
  longitud: number | null;
  subtotal: number;
  importeEnvio: number;
  tarifaServicio: number;
  total: number;
  estatus: string;
  llegadaEstimada: string | null;
  fechaCreacion: string;
  fechaActualizacion: string | null;
  urlGoogleMapsNegocio: string | null;
  seguimientos: Seguimiento[];
  detalles: DetallePedido[];
}

export interface CreatePedidoDto {
  clienteId: number;
  negocioId: number;
  empleadoId?: number | null;
  direccionEntrega: string;
  latitud?: number | null;
  longitud?: number | null;
  subtotal: number;
  importeEnvio: number;
  tarifaServicio: number;
  total: number;
  llegadaEstimada?: string | null;
  detalles?: CreatePedidoDetalleDto[];
}

export interface UpdatePedidoDto {
  empleadoId?: number | null;
  direccionEntrega: string;
  latitud?: number | null;
  longitud?: number | null;
  total: number;
  estatus: string;
  llegadaEstimada?: string | null;
}

export interface UpdateEstatusDto {
  estatus: string;
  descripcion?: string | null;
  latitud?: number | null;
  longitud?: number | null;
}

class PedidosService {
  private baseUrl: string;

  constructor() {
    this.baseUrl = import.meta.env.VITE_API_BASE_URL || 'https://localhost:7123/api';
  }

  private getHeaders(requireAuth = true, hasBody = false): Record<string, string> {
    const headers: Record<string, string> = {};
    if (hasBody) {
      headers['Content-Type'] = 'application/json';
    }
    if (requireAuth) {
      const token = localStorage.getItem('authToken');
      if (token) {
        headers.Authorization = `Bearer ${token}`;
      }
    }
    return headers;
  }

  private async request<T>(endpoint: string, options: RequestInit = {}, requireAuth = true): Promise<T> {
    const url = `${this.baseUrl}${endpoint}`;
    const response = await fetch(url, {
      ...options,
      headers: this.getHeaders(requireAuth, !!options.body),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({
        message: 'Error en la conexión con el servidor',
      }));
      throw new Error(errorData.message || `Error HTTP: ${response.status}`);
    }

    if (response.status === 204) {
      return {} as T;
    }

    return response.json();
  }

  async getAll(): Promise<Pedido[]> {
    return this.request<Pedido[]>('/pedidos');
  }

  async getById(id: number): Promise<Pedido> {
    return this.request<Pedido>(`/pedidos/${id}`);
  }

  async getByNumeroPedido(numeroPedido: string): Promise<Pedido> {
    return this.request<Pedido>(`/pedidos/numero/${numeroPedido}`, {}, false);
  }

  async getByCliente(clienteId: number): Promise<Pedido[]> {
    return this.request<Pedido[]>(`/pedidos/cliente/${clienteId}`);
  }

  async getByEmpleado(empleadoId: number): Promise<Pedido[]> {
    return this.request<Pedido[]>(`/pedidos/empleado/${empleadoId}`);
  }

  async getByEstatus(estatus: string): Promise<Pedido[]> {
    return this.request<Pedido[]>(`/pedidos/estatus/${estatus}`);
  }

  async create(data: CreatePedidoDto): Promise<Pedido> {
    return this.request<Pedido>('/pedidos', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async update(id: number, data: UpdatePedidoDto): Promise<Pedido> {
    return this.request<Pedido>(`/pedidos/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async updateEstatus(id: number, data: UpdateEstatusDto): Promise<Pedido> {
    return this.request<Pedido>(`/pedidos/${id}/estatus`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async delete(id: number): Promise<void> {
    await this.request<void>(`/pedidos/${id}`, {
      method: 'DELETE',
    });
  }
}

export const pedidosService = new PedidosService();
